import React, { useState } from 'react';
import styled from 'styled-components';

interface StatusUpdateButtonProps {
  workspaceId?: string;
  projectId?: string;
  onUpdate: (workspaceId: string, projectId: string) => Promise<any>;
}

const ButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const UpdateButton = styled.button<{ isUpdating?: boolean }>`
  padding: 10px 20px;
  background: ${props => props.isUpdating ? '#ccc' : '#2196f3'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.isUpdating ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: ${props => props.isUpdating ? '#ccc' : '#1976d2'};
  }
  
  &:active {
    transform: ${props => props.isUpdating ? 'none' : 'translateY(1px)'};
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ResultMessage = styled.div<{ success?: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  padding: 12px;
  background: ${props => props.success ? '#4caf50' : '#f44336'};
  color: white;
  border-radius: 4px;
  font-size: 13px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 300px;
  white-space: nowrap;
`;


// モーダル関連のスタイル
const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  z-index: 10000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  animation: ${props => props.isOpen ? 'fadeIn' : 'fadeOut'} 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 5% auto;
  padding: 20px;
  border: 1px solid #888;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 80vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #aaa;
  
  &:hover {
    color: #000;
  }
`;

const ModalBody = styled.div`
  padding: 10px 0;
`;

const DetailSection = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailSectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.95;
`;

const DetailItem = styled.div`
  margin: 6px 0;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #2196f3;
  font-size: 13px;
  line-height: 1.4;

  .test-hierarchy {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .hierarchy-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .hierarchy-label {
    font-size: 11px;
    color: #6c757d;
    min-width: 80px;
    flex-shrink: 0;
  }

  .hierarchy-value {
    color: #333;
    font-weight: 500;
  }

  .hierarchy-value.case-name {
    font-weight: 600;
    color: #1976d2;
  }
`;

const StatusUpdateButton: React.FC<StatusUpdateButtonProps> = ({
  workspaceId,
  projectId,
  onUpdate
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState<any>(null);
  const [showResult, setShowResult] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // デバッグ用：propsの値を確認
  React.useEffect(() => {
    console.log('StatusUpdateButton props:', { workspaceId, projectId, onUpdate: !!onUpdate });
  }, [workspaceId, projectId, onUpdate]);

  const handleUpdate = async () => {
    console.log('要確認ステータス更新開始', { workspaceId, projectId });
    
    if (!workspaceId || !projectId) {
      alert('ワークスペースIDとプロジェクトIDが必要です');
      return;
    }

    setIsUpdating(true);
    setUpdateResult(null);
    setShowResult(false);
    setShowModal(false); // モーダルも閉じる

    try {
      console.log('onUpdateを呼び出し中...');
      const result = await onUpdate(workspaceId, projectId);
      console.log('更新結果:', result);
      console.log('詳細情報の有無:', result.details ? 'あり' : 'なし');
      if (result.details) {
        console.log('詳細情報内容:', {
          added: result.details.added?.length || 0,
          removed: result.details.removed?.length || 0,
          errors: result.details.errors?.length || 0
        });
      }
      setUpdateResult(result);
      
      // 成功時は直接モーダルを表示
      if (result.success) {
        setShowModal(true);
      } else {
        // エラー時のみポップアップ表示
        setShowResult(true);
        setTimeout(() => {
          setShowResult(false);
        }, 5000);
      }
    } catch (error) {
      console.error('ステータス更新エラー:', error);
      const errorMessage = error instanceof Error ? error.message : 'ステータス更新に失敗しました';
      setUpdateResult({
        success: false,
        message: `エラー: ${errorMessage}`,
        total: 0,
        updated: 0,
        skipped: 0,
        errors: 0
      });
      setShowResult(true);
      
      setTimeout(() => {
        setShowResult(false);
      }, 5000);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <ButtonContainer>
      <UpdateButton
        onClick={() => {
          console.log('ボタンがクリックされました！');
          handleUpdate();
        }}
        isUpdating={isUpdating}
        disabled={isUpdating || !workspaceId || !projectId}
        title={!workspaceId || !projectId ? `テスト結果を検索してから実行してください (workspace: ${workspaceId}, project: ${projectId})` : '要確認ステータスを最新化'}
      >
        {isUpdating && <Spinner />}
        要確認ステータス最新化
        {(!workspaceId || !projectId) && (
          <span style={{ fontSize: '10px', display: 'block' }}>
            (無効: W={workspaceId || 'なし'}, P={projectId || 'なし'})
          </span>
        )}
      </UpdateButton>
      
      {/* エラー時のみメッセージ表示 */}
      {showResult && updateResult && !updateResult.success && (
        <ResultMessage success={false}>
          <div>{updateResult.message}</div>
        </ResultMessage>
      )}
      
      {/* モーダル */}
      <Modal isOpen={showModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              🔍 要確認ステータス更新完了
              {updateResult && updateResult.updated > 0 && (
                <span style={{ fontSize: '14px', marginLeft: '10px', color: '#4caf50' }}>
                  ({updateResult.updated}件更新)
                </span>
              )}
            </ModalTitle>
            <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
          </ModalHeader>
          <ModalBody>
            {updateResult && (
              <>
                {/* サマリー情報 */}
                <DetailSection>
                  <DetailSectionTitle>📊 更新サマリー</DetailSectionTitle>
                  <DetailItem>
                    <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                      対象: {updateResult.total}件 | 
                      更新: {updateResult.updated}件 | 
                      スキップ: {updateResult.skipped}件 | 
                      エラー: {updateResult.errors}件
                    </div>
                  </DetailItem>
                </DetailSection>
                
                {/* 詳細情報 */}
                {(!updateResult.details || 
                  ((!updateResult.details.added || updateResult.details.added.length === 0) &&
                   (!updateResult.details.removed || updateResult.details.removed.length === 0) &&
                   (!updateResult.details.errors || updateResult.details.errors.length === 0))) ? (
                  <DetailSection>
                    <DetailSectionTitle>ℹ️ 更新対象なし</DetailSectionTitle>
                    <DetailItem>
                      <div className="case-name">すべてのテストケースは既に適切なステータスです</div>
                      <div className="reason">要確認フラグの変更が必要なケースはありませんでした</div>
                    </DetailItem>
                  </DetailSection>
                ) : (
                  <>
                    {updateResult.details?.added && updateResult.details.added.length > 0 && (
                      <DetailSection>
                        <DetailSectionTitle>
                          📌 要確認に変更 ({updateResult.details.added.length}件)
                        </DetailSectionTitle>
                        {updateResult.details.added.map((item: any, index: number) => (
                          <DetailItem key={`add-${index}`}>
                            <div className="test-hierarchy">
                              <div className="hierarchy-item">
                                <span className="hierarchy-label">テストラン:</span>
                                <span className="hierarchy-value">{item.testRunName}</span>
                              </div>
                              {item.testSuiteName && (
                                <div className="hierarchy-item">
                                  <span className="hierarchy-label">テストスイート:</span>
                                  <span className="hierarchy-value">{item.testSuiteName}</span>
                                </div>
                              )}
                              <div className="hierarchy-item">
                                <span className="hierarchy-label">テストケース:</span>
                                <span className="hierarchy-value case-name">{item.testCaseName}</span>
                              </div>
                            </div>
                          </DetailItem>
                        ))}
                      </DetailSection>
                    )}

                    {updateResult.details?.removed && updateResult.details.removed.length > 0 && (
                      <DetailSection>
                        <DetailSectionTitle>
                          ✅ 要確認を解除 ({updateResult.details.removed.length}件)
                        </DetailSectionTitle>
                        {updateResult.details.removed.map((item: any, index: number) => (
                          <DetailItem key={`remove-${index}`}>
                            <div className="test-hierarchy">
                              <div className="hierarchy-item">
                                <span className="hierarchy-label">テストラン:</span>
                                <span className="hierarchy-value">{item.testRunName}</span>
                              </div>
                              {item.testSuiteName && (
                                <div className="hierarchy-item">
                                  <span className="hierarchy-label">テストスイート:</span>
                                  <span className="hierarchy-value">{item.testSuiteName}</span>
                                </div>
                              )}
                              <div className="hierarchy-item">
                                <span className="hierarchy-label">テストケース:</span>
                                <span className="hierarchy-value case-name">{item.testCaseName}</span>
                              </div>
                            </div>
                          </DetailItem>
                        ))}
                      </DetailSection>
                    )}

                    {updateResult.details?.errors && updateResult.details.errors.length > 0 && (
                      <DetailSection>
                        <DetailSectionTitle>
                          ❌ エラー ({updateResult.details.errors.length}件)
                        </DetailSectionTitle>
                        {updateResult.details.errors.map((item: any, index: number) => (
                          <DetailItem key={`error-${index}`}>
                            <div className="case-name">{item.testCaseName}</div>
                            <div className="reason" style={{ color: '#d32f2f' }}>エラー: {item.error}</div>
                          </DetailItem>
                        ))}
                      </DetailSection>
                    )}
                  </>
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </ButtonContainer>
  );
};

export default StatusUpdateButton;